import React from 'react';
import styled from 'styled-components';
import { ErrorMessage, LabelText } from './Typography';
import { tokens } from "../../../public/tokens.js";

const { colors, typography, effects } = tokens;

const CheckboxContainer = styled.div`
  margin-bottom: ${props => props.marginBottom || '0'};
`;

const CheckboxWrapper = styled.label`
  display: flex;
  align-items: flex-start;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  margin-bottom: 0;
`;

const CheckboxInput = styled.input`
  position: absolute;
  opacity: 0;
  z-index: -1;
  
  &:checked + .checkbox-visual {
    background: ${tokens.colors.theme['topbar-color']};
    border-color: ${tokens.colors.theme['topbar-color']};
  }
  
  &:checked + .checkbox-visual::after {
    display: block;
  }
  
  &:focus + .checkbox-visual {
    border-color: ${tokens.colors.theme['highlight-color']};
    box-shadow: ${tokens.effects.effects.shadow.hover};
  }
  
  &:disabled + .checkbox-visual {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CheckboxVisual = styled.div`
  position: relative;
  width: 20px;
  height: 20px;
  min-width: 20px;
  border: 1px solid ${props => props.hasError ? colors.states.alert.default : colors.surfaces['border-divider']};
  border-radius: var(--border-radius);
  background: ${colors.neutral.white};
  cursor: pointer;
  margin-right: 10px;
  margin-top: 4px;
  
  &::after {
    content: "";
    position: absolute;
    display: none;
    left: 6px;
    top: 2px;
    width: 5px;
    height: 10px;
    border: solid ${colors.neutral.white};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const CheckboxLabel = styled(LabelText)`
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  color: ${props => {
    if (props.hasError) return colors.states.alert.default;
    if (props.color) return props.color;
    return colors.text.primary;
  }};
  font-size: ${props => {
    if (props.size === 'small') return `${typography.fontSize['md-12px']}px`;
    return `${typography['body-small'].Regular.fontSize}px`;
  }};
  line-height: ${typography['body-small'].Regular.lineHeight};
  margin: 0;
  display: block;
`;


/**
 * @param {string} id - Unique identifier for the checkbox input (required)
 * @param {string} [name] - Name attribute for form submission
 * @param {boolean} checked - Controlled checked state of checkbox (required)
 * @param {function} onChange - Change handler: (e: React.ChangeEvent<HTMLInputElement>) => void (required)
 * @param {function} [onBlur] - Blur handler for form validation
 * @param {boolean} [disabled=false] - Disables checkbox interaction
 * @param {ReactNode} label - Checkbox label content (text, JSX, etc.)
 * @param {string} [error] - Error message for validation
 * @param {boolean} [touched] - Form touched state for validation display
 * @param {string} [marginBottom] - Custom margin bottom spacing
 * @param {'normal'|'small'} [size='normal'] - Label font size: 'normal' (14px) or 'small' (12px)
 * @param {string} [color] - Custom label text color (CSS variable or hex)
 * @param {boolean} [required=false] - Marks checkbox as required field
 * @param {boolean} [showError=false] - Controls error message visibility
 * @param {string} [className] - Additional CSS class names
 * 
 * @returns {JSX.Element} Styled checkbox component with label and error handling
 */

const CustomCheckbox = ({
  id,
  name,
  checked,
  onChange,
  onBlur,
  disabled,
  label,
  error,
  touched,
  marginBottom,
  size = 'normal',
  color = "",
  required = false,
  showError = false,
  className
}) => {
  const hasError = (error && touched);
  const isDisabled = disabled;

  return (
    <CheckboxContainer marginBottom={marginBottom} className={className}>
      <CheckboxWrapper 
        htmlFor={id}
        disabled={isDisabled}
      >
        <CheckboxInput
          type="checkbox"
          id={id}
          name={name}
          checked={checked}
          onChange={onChange}
          onBlur={onBlur}
          disabled={isDisabled}
          required={required}
        />
        <CheckboxVisual 
          className="checkbox-visual"
          hasError={hasError}
        />
        
        <CheckboxLabel
          htmlFor={id}
          disabled={isDisabled}
          hasError={hasError}
          size={size}
          color={color}
        >
          {label}
        </CheckboxLabel>
      </CheckboxWrapper>
      
      {hasError && showError && error && (
        <ErrorMessage>
          {error}
        </ErrorMessage>
      )}
    </CheckboxContainer>
  );
};

export default CustomCheckbox;